import React, { useState, useEffect, useContext } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, {
  PaginationProvider,
  PaginationListStandalone,
} from "react-bootstrap-table2-paginator";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { apiUrl } from "../../Config/Url";
import ListAdminButtons from "../Common/ListAdminButtons";
import { useHistory } from "react-router-dom";
import * as dateHelper from "../../Helpers/DateHelper";
import DeleteModal from "../Certificate/DeleteModal";
import { certificateService } from "../../Services/certificate.service";
import ResultModal from "../Common/ResultModal";
import ToolkitProvider, { Search } from "react-bootstrap-table2-toolkit";
import { AuthContext } from "../../Contexts/AuthContext";
import { roles } from "../../Config/Roles";
import SpinnerLoading from "../Common/SpinnerLoading";
import Tooltip from "../../Components/Common/Tooltip";
import { GlobalContext } from "../../Contexts/GlobalContextProvider";

const CertificateTab = ({ tenantId }) => {
  const { isMobile, locTab } = useContext(GlobalContext);
  const { t: translator } = useTranslation();
  const { roleId } = useContext(AuthContext);
  const { master, admin, support, owner } = roles;
  const [data, setData] = useState([]);
  const [update, setUpdate] = useState(new Date());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState({
    title: "",
    text: "",
    show: false,
    btnClass: "",
  });
  const [search, setSearch] = useState({
    searchText: "",
    sortName: "Name",
    isAsc: true,
  });
  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 10,
    pageIndex: 0,
  });

  const history = useHistory();

  useEffect(() => {
    if (locTab === "certificates") {
      setIsLoading(true);
      axios
        .put(`${apiUrl}/Certificate/search`, {
          tenantId: Number(tenantId),
          searchText: search.searchText,
          sortName: search.sortName,
          isAsc: search.isAsc,
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        })
        .then(res => {
          setData(res.data.certificates);
          setIsLoading(false);
        })
        .catch(err => console.log(err.response));
    }
  }, [update, locTab]);

  const handleToUpdate = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/update-certificate/${selectedItems}`);
  };

  const handleToDetail = () => {
    if (selectedItems.length !== 1) return false;
    history.push(`/certificates/${selectedItems}`);
  };

  const handleToUpload = () => {
    history.push("/upload-certificate");
  };
  const handleToUploadMini = () => {
    history.push("/upload-mini-certificate");
  };

  const handleToDownload = () => {
    certificateService.download(selectedItems).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "certificates.zip");
      document.body.appendChild(link);
      link.click();
    });
  };

  const handleToDecryptPassword = () => {
    certificateService
      .decryptPassword(selectedItems[0])
      .then((res) => {
        setModal({
          show: true,
          title: translator("Certificate-DecryptPassword-Title"),
          text: res.data.password,
          btnClass: "btn btn-primary",
        });
      })
      .catch((err) => {
        setModal({
          show: true,
          title: translator("CertificateUpdate-UnsuccesModal-Title"),
          text: translator(err?.response?.data?.message || "CertificateUpdate-UnsuccesModal-Text"),
          btnClass: "btn btn-primary",
        });
      });
  };

  const handleClose = () => {
    setModal({ show: false });
  };

  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedItems(selectedItems => [...selectedItems, row.certificateId]);
    } else {
      setSelectedItems(selectedItems.filter(p => p !== row.certificateId));
    }
  };

  const handleOnSelectAll = (isSelect, rows) => {
    if (isSelect) {
      setSelectedItems(data.map(x => x.certificateId));
    } else {
      setSelectedItems([]);
    }
  };

  function actionFormatter(cell, row) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            checked={selectedItems.find(x => x === row.certificateId)}
            onChange={e => handleOnSelect(row, e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function actionHeaderFormatter(cell, rows) {
    return (
      <div className="action">
        <label className="labelCont">
          <input
            type="checkbox"
            checked={
              data &&
              data.length > 0 &&
              !data
                .map(p => p.certificateId)
                .some(p => selectedItems.indexOf(p) < 0)
            }
            onChange={e => handleOnSelectAll(e.target.checked)}
          />
          <span className="checkmark"></span>
        </label>
      </div>
    );
  }

  function dateFormatter(cell, rows) {
    return dateHelper.formatUtcToDate(cell);
  }

  const confirmDelete = () => {
    let deletePromises = [];

    selectedItems.forEach(x => {
      deletePromises.push(certificateService.deleteCert(x));
    });

    Promise.all(deletePromises)
      .then(data => {
        setShowDeleteModal(false);
        setModal({
          ...modal,
          title: "Certificate-ResultModal-DeleteSuccess-Title",
          text: "Certificate-ResultModal-DeleteSuccess-Text",
          show: true,
          btnClass: "btn btn-primary",
        });
        setUpdate(new Date());
      })
      .catch(err =>
        setModal({
          ...modal,
          title: "Certificate-ResultModal-DeleteError-Title",
          text: "Certificate-ResultModal-DeleteError-Text",
          show: true,
          btnClass: "btn btn-primary",
        }),
      );
  };

  const handleHideDeleteModal = () => setShowDeleteModal(false);

  const { SearchBar } = Search;

  const columns = [
    {
      dataField: "certificateId",
      text: "",
      headerStyle: (colum, colIndex) => {
        return { width: "45px", textAlign: "center" };
      },
      headerFormatter: actionHeaderFormatter,
      formatter: actionFormatter,
      hidden: [master, admin, support, owner].includes(Number(roleId))
        ? false
        : true,
    },
    {
      dataField: "name",
      text: translator("TenantDetail-CertificateTab-Name"),
    },
    {
      dataField: "validFrom",
      formatter: dateFormatter,
      text: translator("TenantDetail-CertificateTab-ValidFrom"),
      hidden: Boolean(isMobile),
    },
    {
      dataField: "validTo",
      formatter: dateFormatter,
      text: translator("TenantDetail-CertificateTab-ValidTo"),
      hidden: Boolean(isMobile),
    },
  ];

  const options = { custom: true, totalSize: data.length, sizePerPage: 8 };

  return (
    <>
      <div className="p-2">
        <div className="container-fluid p-0">
          <div className="card-body pl-0 mb-4">
            <ToolkitProvider
              keyField="certificateId"
              data={data}
              columns={columns}
              search>
              {props => (
                <div>
                  <SearchBar
                    placeholder={translator("Certificate-Tab-Search")}
                    {...props.searchProps}
                  />{" "}
                  <Tooltip text={translator("Tabs-Search")} />
                  <ListAdminButtons
                    selectedItemsLength={selectedItems.length}
                    handleToAdd={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? handleToUpload
                        : null
                    }
                    handleToDetail={handleToDetail}
                    handleToUpdate={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? handleToUpdate
                        : null
                    }
                    handleDelete={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? () => setShowDeleteModal(true)
                        : null
                    }
                    handleToMiniAdd={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? handleToUploadMini
                        : null
                    }

                    handleToDownload={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? handleToDownload
                        : null
                    }
                    handleToDecryptPassword={
                      [master, admin, support, owner].includes(Number(roleId))
                        ? handleToDecryptPassword
                        : null
                    }
                  />
                  <PaginationProvider pagination={paginationFactory(options)}>
                    {({ paginationProps, paginationTableProps }) => (
                      <div>
                        {isLoading && (
                          <div className="dashboard-card-spinner">
                            <SpinnerLoading />
                          </div>
                        )}
                        {!isLoading && (
                          <BootstrapTable
                            keyField="certificateId"
                            data={data}
                            loading
                            columns={columns}
                            classes={"table-sm"}
                            rowClasses="boostrap-table-row"
                            noDataIndication={translator("No-Data")}
                            {...paginationTableProps}
                            {...props.baseProps}
                          />
                        )}

                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    )}
                  </PaginationProvider>
                </div>
              )}
            </ToolkitProvider>
          </div>
        </div>
      </div>
      <DeleteModal
        show={showDeleteModal}
        handleHide={handleHideDeleteModal}
        title={translator("Certificate-DeleteModal-Title")}
        text={translator("Certificate-DeleteModal-Text")}
        confirmDelete={confirmDelete}
      />
      <ResultModal
        show={modal.show}
        title={modal.title}
        text={modal.text}
        btnClass={modal.btnClass}
        handleClose={handleClose}
      />
    </>
  );
};

export default CertificateTab;
